/**
 * The feedback points of a text check: each has a text and how much it matters.
 * Results from before severities existed are plain strings; those get no level.
 */

/** Levels, most serious first, with the wording and colour shown to the worker. */
export const FEEDBACK_SEVERITIES = {
  urgent: {
    label: "Moet aangepast",
    color: "error",
    icon: "mdi-alert-circle",
  },
  important: {
    label: "Belangrijk",
    color: "warning",
    icon: "mdi-alert",
  },
  note: {
    label: "Aandachtspunt",
    color: undefined,
    icon: "mdi-information-outline",
  },
};

/**
 * Feedback points in one shape, whatever the result looks like.
 *
 * @param {Array<string|{ text: string, severity?: string }>} bullets
 * @returns {Array<{ text: string, severity: string|null }>} severity is null for a
 *   point without a (known) level
 */
export function feedbackPoints(bullets) {
  return (bullets || [])
    .map((bullet) =>
      typeof bullet === "string"
        ? { text: bullet, severity: null }
        : {
            text: bullet?.text || "",
            severity:
              bullet?.severity in FEEDBACK_SEVERITIES ? bullet.severity : null,
            // Where the point comes from and the words concerned, when given
            ...(bullet?.source ? { source: bullet.source } : {}),
            ...(bullet?.quote ? { quote: bullet.quote } : {}),
            ...(bullet?.check ? { check: bullet.check } : {}),
          },
    )
    .filter((point) => point.text.trim() !== "");
}

/** Label, colour and icon of a level; null for a point without one. */
export function severityStyle(severity) {
  return FEEDBACK_SEVERITIES[severity] || null;
}

/**
 * How many points there are per level, for a one-line summary
 * ("1 moet aangepast · 2 aandachtspunten").
 */
export function severitySummary(points) {
  const wording = {
    urgent: ["moet aangepast", "moeten aangepast"],
    important: ["belangrijk", "belangrijk"],
    note: ["aandachtspunt", "aandachtspunten"],
  };
  return Object.keys(FEEDBACK_SEVERITIES)
    .map((severity) => {
      const count = (points || []).filter(
        (point) => point.severity === severity,
      ).length;
      return count
        ? `${count} ${wording[severity][count === 1 ? 0 : 1]}`
        : null;
    })
    .filter(Boolean)
    .join(" · ");
}

/**
 * How an admin writes a check: a line in the instruction text that starts with a
 * level. Shown as a hint under the text fields.
 */
export const CHECK_SYNTAX_HINT =
  "Begin een regel met [urgent], [belangrijk] of [aandacht] om er een controlepunt van te maken. Schrijf wat erin moet staan, of begin met 'Geen …' voor wat er niet in mag staan.";

// Where a point comes from, in the order the text check works and shows them.
const SOURCE_ORDER = ["report_type", "general", "type", "ai"];

/**
 * The points of a text check grouped by where they come from: the report type first,
 * then the general checks, then the checks of this report type. Points the AI wrote
 * by itself (a template without checks) and points from older results come last,
 * without a heading of their own when they are the only group.
 *
 * @param {Array} bullets - feedback points, with an optional `source`
 * @param {{ typeName?: string }} [options] - the report type, for the heading
 * @returns {Array<{ source: string, title: string, points: Array }>}
 */
export function groupedFeedback(bullets, { typeName = "" } = {}) {
  const titles = {
    report_type: "Type rapport",
    general: "Algemeen",
    type: typeName ? `Voor het type ${typeName}` : "Voor dit type rapport",
    ai: "Overige opmerkingen",
  };
  const groups = new Map();
  (bullets || []).forEach((bullet) => {
    const [point] = feedbackPoints([bullet]);
    if (!point) return;
    const source = SOURCE_ORDER.includes(bullet?.source) ? bullet.source : "ai";
    if (!groups.has(source)) groups.set(source, []);
    groups.get(source).push({
      ...point,
      quote: bullet?.quote || null,
      check: bullet?.check || null,
    });
  });
  const result = SOURCE_ORDER.filter((source) => groups.has(source)).map(
    (source) => ({ source, title: titles[source], points: groups.get(source) }),
  );
  // One group of the AI's own points needs no heading.
  if (result.length === 1 && result[0].source === "ai") result[0].title = "";
  return result;
}
